import type {
  SearchProvider,
  SearchRun,
  SearchSiteView,
  SearchSourceView,
} from '../../../api/src/search-metrics/search-metrics.types';

export const loaderStateNames = {
  queued: 'В очереди',
  running: 'Выполняется',
  complete: 'Завершено',
  partial: 'Частичный результат',
  error: 'Ошибка',
  superseded: 'Заменено новым планом',
};
const dimensionNames = { total: 'Итоги', query: 'Запросы', page: 'Страницы' };
type RecordValue = Record<string, unknown>;
function record(value: unknown): value is RecordValue {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** Checkpoints count processed parts, including failures; they are not an overall ETA. */
export function loaderProgress(run: SearchRun) {
  if (!run.planJson) return null;
  let plan: unknown, evidence: unknown;
  try {
    plan = JSON.parse(run.planJson);
    evidence = JSON.parse(run.evidenceJson);
  } catch {
    return null;
  }
  if (
    !Array.isArray(plan) ||
    !plan.length ||
    !record(evidence) ||
    !plan.every(
      (part) =>
        record(part) &&
        typeof part.dimension === 'string' &&
        Object.hasOwn(dimensionNames, part.dimension) &&
        typeof part.dateFrom === 'string' &&
        typeof part.dateTo === 'string',
    ) ||
    !Object.entries(evidence).every(
      ([key, part]) =>
        /^(0|[1-9]\d*)$/.test(key) &&
        Number(key) < plan.length &&
        record(part) &&
        (part.done === undefined || typeof part.done === 'boolean') &&
        (part.error === undefined || typeof part.error === 'string'),
    )
  )
    return null;
  const parts = Object.values(evidence) as RecordValue[];
  return {
    total: plan.length,
    processed: parts.filter((part) => part.done === true).length,
    failed: parts.filter((part) => part.done === true && Boolean(part.error)).length,
    dimensions: [
      ...new Set(plan.map((part) => dimensionNames[part.dimension as keyof typeof dimensionNames])),
    ],
    errors: parts.flatMap((part) =>
      typeof part.error === 'string' && part.error ? [part.error] : [],
    ),
  };
}

export function metricsLoaderState(sites: SearchSiteView[]) {
  const providers = new Map<
    SearchProvider,
    { provider: SearchProvider; running: number; queued: number }
  >();
  const rows = sites.flatMap((site) =>
    site.sources.map((source: SearchSourceView) => {
      const run = source.lastRun;
      const progress = run ? loaderProgress(run) : null;
      const problems = [
        ...new Set([
          ...(!source.connectionIds.length ? ['Аккаунт не подключён'] : []),
          ...(source.warnings ?? []),
          ...(run?.error ? [run.error] : []),
          ...(progress?.errors ?? []),
          ...(run && !Object.hasOwn(loaderStateNames, run.status)
            ? ['Неизвестное состояние загрузки']
            : []),
        ]),
      ];
      const attention = problems.length > 0 || run?.status === 'error' || run?.status === 'partial';
      const counts = providers.get(source.property.provider) ?? {
        provider: source.property.provider,
        running: 0,
        queued: 0,
      };
      if (run?.status === 'running' || run?.status === 'queued') counts[run.status]++;
      providers.set(counts.provider, counts);
      return { siteKey: site.siteKey, source, run, progress, problems, attention };
    }),
  );
  const counts = [...providers.values()];
  const running = counts.reduce((sum, provider) => sum + provider.running, 0);
  const queued = counts.reduce((sum, provider) => sum + provider.queued, 0);
  // Show work and problems first, preserving source order within each category.
  const priority = (row: (typeof rows)[number]) =>
    row.run?.status === 'running' ? 0 : row.run?.status === 'queued' ? 1 : row.attention ? 2 : 3;
  rows.sort((a, b) => priority(a) - priority(b));
  return {
    providers: counts,
    rows,
    running,
    queued,
    active: running + queued > 0,
    attention: rows.filter((row) => row.attention).length,
  };
}
export type MetricsLoaderState = ReturnType<typeof metricsLoaderState>;
